import Grid from "@mui/material/Grid";
import Pagination from "@mui/material/Pagination";
import PaginationItem from "@mui/material/PaginationItem";
import Typography from "@mui/material/Typography";
import { ExternalLink } from "ExternalLink";
import { useAppSelector } from "app/hooks";
import { FormSection } from "components/FormControls/FormSection";
import { Search } from "components/FormControls/Select/Search";
import { Breadcrumbs, TopHeader } from "components/Header/TopHeader/TopHeader";
import { ReactComponent as PlusSquareIcon } from "components/Icons/plus-square.svg";
import { OLTabs } from "components/Tabs/OLTabs";
import { useMessage } from "hooks/intl";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { selectDefaultLanguageData } from "reduxState/translations/translationsSlice";
import {
  AchievementRuleTypes,
  AchievementTemplate,
  AchievementTriggers,
} from "types/achievements";
import { links } from "utilities/links";
import { useStyles } from "./AchievementTemplates.styles";
import { EmptySearchView } from "./EmptySearchView";
import { TemplateBadge } from "./TemplateBadge";
import { TemplateBox } from "./TemplateBox";
import { getAchievementTemplatesData } from "./helpers";

const tabs = {
  all: "all",
  transactions: "transactions",
  customEvents: "customEvents",
  referrals: "referrals",
} as const;

type TabLabel = (typeof tabs)[keyof typeof tabs];

const tabsList = Object.keys(tabs) as TabLabel[];

export function AchievementTemplates() {
  const { message } = useMessage();
  const classes = useStyles();
  const navigate = useNavigate();
  const defaultLang = useAppSelector(selectDefaultLanguageData);

  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState<TabLabel | false>(tabs.all);
  const [currentTemplatesList, setCurrentTemplatesList] = useState<
    AchievementTemplate[]
  >([]);
  const [searchedValue, setSearchedValue] = useState("");

  const achievementTemplatesData = getAchievementTemplatesData(
    defaultLang.code,
  );

  const templatesPerPage = 7;

  const isPaginationVisible = currentTemplatesList.length > 7;

  const filterTemplates = (
    items: AchievementTemplate[],
    searchQuery: string,
    defaultLang: string,
  ) => {
    return [...items].filter(
      (item) =>
        message({ id: item.name })
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        (item.translations &&
          message({ id: item.translations[defaultLang]?.description })
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase())),
    );
  };

  const searchedTemplatesList = filterTemplates(
    achievementTemplatesData,
    searchedValue,
    defaultLang.code,
  );

  const paginatedTemplatesList = currentTemplatesList.slice(
    (currentPage - 1) * templatesPerPage,
    currentPage * templatesPerPage,
  );

  const isTemplateListEmpty =
    searchedValue.length > 0 && currentTemplatesList.length === 0;

  const achievementTemplatesWithTransactionsTrigger =
    achievementTemplatesData.filter((achievement) =>
      achievement.rules
        .map(
          (rule) => rule.type === AchievementRuleTypes.DIRECT && rule.trigger,
        )
        .includes(AchievementTriggers.TRANSACTION),
    );

  const achievementTemplatesWithCustomEventsTrigger =
    achievementTemplatesData.filter((achievement) =>
      achievement.rules
        .map(
          (rule) => rule.type === AchievementRuleTypes.DIRECT && rule.trigger,
        )
        .includes(AchievementTriggers.CUSTOM_EVENT),
    );

  const achievementTemplatesWithReferralRuleType =
    achievementTemplatesData.filter((achievement) =>
      achievement.rules
        .map((rule) => rule.type)
        .includes(AchievementRuleTypes.REFERRAL),
    );

  const breadcrumbs: Breadcrumbs = {
    parent: {
      label: message({ id: "listOfAchievements" }),
      path: links.achievementsList,
    },
  };

  const getCurrentTemplatesListByActiveTab = () => {
    switch (activeTab) {
      case "all":
        return achievementTemplatesData;
      case "transactions":
        return achievementTemplatesWithTransactionsTrigger;
      case "customEvents":
        return achievementTemplatesWithCustomEventsTrigger;
      case "referrals":
        return achievementTemplatesWithReferralRuleType;
      default:
        return [];
    }
  };

  useEffect(() => {
    setCurrentPage(1);
    if (searchedValue.length === 0) {
      setCurrentTemplatesList(getCurrentTemplatesListByActiveTab());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, searchedValue, defaultLang]);

  useEffect(() => {
    if (searchedValue.length > 0) {
      setCurrentTemplatesList(searchedTemplatesList);
      setActiveTab(false);
    }

    if (searchedValue.length === 0) {
      setActiveTab("all");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchedValue]);

  return (
    <>
      <TopHeader
        clearLink={links.achievementsList}
        title={message({ id: "addAchievement" })}
        breadcrumbs={breadcrumbs}
      />
      <div className={classes.form}>
        <FormSection
          title={message({ id: "createAchievement" })}
          subTitle={
            <Typography className={classes.sectionSubheader} mb={8}>
              {message(
                { id: "createAchievementInfo" },
                {
                  link: (
                    <ExternalLink
                      className={classes.link}
                      path="https://help.openloyalty.io/main-features/achievements"
                    >
                      {message({
                        id: "learnMoreAboutAchievements",
                      })}
                    </ExternalLink>
                  ),
                },
              )}
            </Typography>
          }
        >
          <div className={classes.tabsContainer}>
            <OLTabs
              items={tabsList}
              value={activeTab}
              onChange={(_: React.SyntheticEvent, newValue: TabLabel) =>
                setActiveTab(newValue)
              }
              data-test-id="tabs"
            />
            <Search
              searchedValue={searchedValue}
              setSearchedValue={setSearchedValue}
              className={classes.searchInput}
              placeholder={`${message({ id: "search" })}...`}
            />
          </div>
          <Grid container spacing={8}>
            {isTemplateListEmpty ? (
              <EmptySearchView />
            ) : (
              <>
                <Grid item xs={6}>
                  <TemplateBox
                    butttonLabel="startFromScratch"
                    className={classes.highlightedBox}
                    badge={<PlusSquareIcon className={classes.addIcon} />}
                    title={message({ id: "customConfiguration" })}
                    description={message({ id: "createCustomAchievementInfo" })}
                    onClick={() => navigate(links.addAchievement)}
                  />
                </Grid>
                {paginatedTemplatesList.map((achievement, index) => (
                  <Grid item xs={6}>
                    <TemplateBox
                      id={index}
                      badge={<TemplateBadge rules={achievement.rules} />}
                      title={message({ id: achievement.name })}
                      description={
                        message({
                          id: achievement.translations[defaultLang.code]
                            ?.description,
                        }) || ""
                      }
                      onClick={() => {
                        navigate(links.addAchievement, {
                          state: { template: achievement },
                        });
                      }}
                    />
                  </Grid>
                ))}
              </>
            )}
          </Grid>
          {isPaginationVisible && (
            <div className={classes.pagination}>
              <Pagination
                shape="rounded"
                page={currentPage}
                onChange={(_, value) => setCurrentPage(value)}
                count={Math.ceil(
                  currentTemplatesList.length / templatesPerPage,
                )}
                renderItem={(props) => (
                  <PaginationItem
                    {...props}
                    classes={{
                      root: classes.paginationItem,
                      selected: classes.activePaginationItem,
                    }}
                  />
                )}
              />
            </div>
          )}
        </FormSection>
      </div>
    </>
  );
}
